import { ChangeEvent, Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import styles from './style.module.scss';
import { errorMessages } from '../../configs';
import { HideIcon, ShowIcon } from '../../assets/icons';
import { useWindowEventListener } from '../../hooks/useWindowEventListener';
import {
    validateGPA,
    validateEmail,
    validateZipCode,
    validateNumberRange,
    isValidURL,
} from '../../utils/generalHelpers';

interface Props {
    type: string;
    list?: any[];
    value: string;
    label?: string;
    onChange?: any;
    error?: string;
    onItemClick?: any;
    focused?: boolean;
    hasError?: boolean;
    listShow?: boolean;
    listIdKey?: string;
    disabled?: boolean;
    maxLength?: number;
    isRequired?: boolean;
    placeholder?: string;
    listTitleKey?: string;
    inputClassName?: string;
    wrapperClassName?: string;
    listTitleSuffixKey?: string;
    setIsValid?: Dispatch<SetStateAction<boolean>>;
    validation?: 'Email' | 'GPA' | 'ZipCode' | 'PhoneNumber' | 'NumberRange' | 'URL';
    inputMode?: "text" | "search" | "email" | "tel" | "url" | "none" | "numeric" | "decimal";
};

function Input(props: Props) {
    const {
        type,
        list,
        label,
        value,
        error,
        focused,
        onChange,
        listShow,
        hasError,
        disabled,
        maxLength,
        inputMode,
        validation,
        setIsValid,
        listIdKey,
        isRequired,
        onItemClick,
        placeholder,
        listTitleKey,
        inputClassName,
        wrapperClassName,
        listTitleSuffixKey,
    } = props;

    const [showList, setShowList] = useWindowEventListener(closeList);
    const [errorMessage, setErrorMessage] = useState(error || '');
    const [localType, setLocalType] = useState(type);
    const [listBottom, setListBottom] = useState('unset');
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!ref.current) return;
        const space = window.innerHeight - ref.current.offsetTop + ref.current.offsetHeight;
        if (space < 360) {

            setListBottom('46px');
        } else {
            setListBottom('unset');
        }
    }, []);

    const setValue = (e: ChangeEvent<HTMLInputElement>) => {
        onChange(e.target.value);

        if (e.target.value && listShow && list && list.length > 0) {
            setShowList(true);
        } else {
            setShowList(false);
        }

        if (!e.target.value) {
            setIsValid && setIsValid(true);
            setErrorMessage('');
            return;
        }

        let message = '';

        switch (validation) {
            case 'Email': {
                if (validateEmail(e.target.value)) {
                    setIsValid && setIsValid(true);
                    message = '';
                } else {
                    setIsValid && setIsValid(false);
                    message = errorMessages.email;
                }
                break;
            }
            case 'NumberRange': {
                if (validateNumberRange(e.target.value)) {
                    setIsValid && setIsValid(true);
                    message = '';
                } else {
                    setIsValid && setIsValid(false);
                    message = errorMessages.salaryRange;
                }
                break;
            }
            case 'GPA': {
                if (validateGPA(e.target.value)) {
                    setIsValid && setIsValid(true);
                    message = '';
                } else {
                    setIsValid && setIsValid(false);
                    message = errorMessages.GPA;
                }
                break;
            }
            case 'ZipCode': {
                if (validateZipCode(e.target.value)) {
                    setIsValid && setIsValid(true);
                    message = '';
                } else {
                    setIsValid && setIsValid(false);
                    message = errorMessages.zipCode;
                }
                break;
            }
            case 'URL': {
                if (isValidURL(e.target.value)) {
                    setIsValid && setIsValid(true);
                    message = '';
                } else {
                    setIsValid && setIsValid(false);
                    message = errorMessages.URL;
                }
                break;
            }
            default: {
                break;
            }
        }

        setErrorMessage(message);
    };

    const showHide = () => {
        const t = (localType === 'text' ? 'password' : 'text');
        setLocalType(t);
    };

    function closeList() {
        setShowList(false);
    };

    const listItemClick = (item: any) => {
        onItemClick(item);
    };

    return (
        <div ref={ref} className={`${styles.wrapper} ${wrapperClassName || ''}`}>
            {label && <span className={styles.label}>{label} {isRequired && <b>*</b>}</span>}
            <div className={styles.inputWrapper}>
                <input
                    value={value}
                    type={localType}
                    name='customInput'
                    autoFocus={focused}
                    onChange={setValue}
                    disabled={disabled}
                    maxLength={maxLength}
                    placeholder={placeholder}
                    className={inputClassName}
                    inputMode={inputMode || 'text'}
                    data-has-error={hasError || Boolean((errorMessage || error))}
                />
                {type === 'password' && (
                    <div className={styles.showHideBTN} onClick={showHide}>
                        {localType === 'password' ?
                            <ShowIcon width={20} height={20} stroke='#ffffff' fill='#74788D' /> :
                            <HideIcon width={20} height={20} stroke='#74788D' fill='#74788D' />}
                    </div>)}
            </div>
            {(errorMessage || error) && (<span className={styles.error}>{errorMessage || error}</span>)}
            {listShow && showList && list && list.length > 0 && listTitleKey && listIdKey && (
                <div className={styles.list} style={{ bottom: listBottom }}>
                    {list.map(item => {
                        return (<div
                            key={item[listIdKey]}
                            className={styles.list_item}
                            onClick={() => listItemClick(item)}
                        >
                            <span>{`${item[listTitleKey]}${listTitleSuffixKey ? ` / ${item[listTitleSuffixKey]}` : ''}`}</span>
                        </div>)
                    })}
                </div>)}
        </div>
    );
};

export default Input;
